import apiClient from './apiClient';

export type RecordingSessionStatus =
  | 'capturing'
  | 'paused'
  | 'stopped'
  | 'committed'
  | 'abandoned';

export type RecordingSessionMode = 'ambient' | 'dictation' | 'protocol';

export type RecordingSessionConsentMethod =
  | 'explicit_tap'
  | 'implied_visit'
  | 'scripted_preamble';

export interface RecordingSessionSoapDraft {
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  chiefComplaint?: string;
  historyPresentIllness?: string;
  diagnoses?: Array<{ code?: string; description: string }>;
}

export interface RecordingSessionMetadata {
  appointmentId?: string;
  roomId?: string;
  notes?: string;
}

/**
 * Shape returned by `POST /recording-sessions/:id/extract-patient-info`.
 * All fields optional — extraction is best-effort and the FE form
 * surfaces only the ones the LLM was confident about. Free-text
 * `chiefComplaint` rides along for the encounter notes / SOAP-seed.
 */
export interface ExtractedPatientInfo {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string; // ISO YYYY-MM-DD when known
  ageYears?: number;    // fallback when only "34" was said
  gender?: 'male' | 'female' | 'other';
  phone?: string;
  email?: string;
  address?: string;
  chiefComplaint?: string;
  /** Convenience: full preferred display name when first/last weren't
   *  cleanly separable (e.g. single-word names common in some
   *  locales). The FE splits on first space when populating. */
  preferredName?: string;
}

/** One persisted diarized turn (recording_sessions.transcript_segments). */
export interface RecordingSessionTranscriptSegmentDTO {
  text: string;
  speaker?: string;
  chunkIndex?: number;
}

export interface RecordingSessionDTO {
  id: string;
  providerId: string;
  patientId: string | null;
  medicalRecordId: string | null;
  status: RecordingSessionStatus;
  sessionMode: RecordingSessionMode;
  startedAt: string;
  endedAt: string | null;
  /** Provenance fields (appointment id, room id, etc.). Encrypted at rest
   *  on the backend; surfaced as plain JSON in the DTO so the FE can
   *  thread the originating appointment back onto the encounter. */
  metadata: RecordingSessionMetadata | null;
  /** ISO timestamp of the most recent resume (or start). null when paused.
   *  Combined with `durationMs` it's the source of truth for elapsed time. */
  lastResumedAt: string | null;
  /** Accumulated *active* time (paused windows excluded). bigint columns
   *  arrive as strings from the JSON layer; coerce in callers. */
  durationMs: number;
  language: string;
  consentGivenAt: string | null;
  consentMethod: RecordingSessionConsentMethod | null;
  transcript: string | null;
  /** Structured speaker-labelled counterpart of `transcript`, rewritten by
   *  the scribe processor alongside every transcript persist. The dock
   *  seeds its live pane from this after a refresh/handover so speaker
   *  chips survive the reload. Null on rows predating the column. */
  transcriptSegments: RecordingSessionTranscriptSegmentDTO[] | null;
  /** Polished transcript from the post-process pass (full-audio
   *  re-transcribe + full-audio diarize). Null until the BG pass
   *  completes; null forever for sessions that errored out of the
   *  post-process. The review page prefers this over `transcript`
   *  for display + Regenerate when present. */
  refinedTranscript: string | null;
  soapDraft: RecordingSessionSoapDraft | null;
  /** Template the doctor picked for this consult, persisted on the
   *  session row. Copied onto medical_records.template_id at commit. */
  templateId: string | null;
  /** Pipeline-stage tracker. Read by the FE recovery driver to decide
   *  whether synth needs (re)driving on a refresh / cross-device
   *  handover. Server-authoritative — preferred over the localStorage
   *  `completionIntent` flag because it survives device switches. */
  stages: RecordingSessionStages | null;
}

export type RecordingSessionStageStatus =
  | 'pending'
  | 'in_flight'
  | 'transcribed'
  | 'synthesized'
  | 'committed'
  | 'failed';

export interface RecordingSessionStages {
  upload?: RecordingSessionStageStatus;
  transcribe?: RecordingSessionStageStatus;
  synth?: RecordingSessionStageStatus;
  commit?: RecordingSessionStageStatus;
  synthAttempts?: number;
  lastReprocessAt?: string;
}

export interface StartRecordingSessionPayload {
  patientId?: string;
  sessionMode?: RecordingSessionMode;
  language?: string;
  consentMethod?: RecordingSessionConsentMethod;
  /** Originating appointment id. Persisted into session metadata and
   *  copied onto the resulting medical_record at commit time so the
   *  appointment tile links to the consult it produced. */
  appointmentId?: string;
  /** Template the doctor picked. Persisted on the session row and
   *  copied onto medical_records.template_id at commit time so the
   *  encounter review page renders the same shape that was scribed
   *  into. */
  templateId?: string;
}

const BASE = '/recording-sessions';

// Backend wraps every response in `{ success, data, message, timestamp }`.
// Earlier calls were returning the envelope directly, leaving `dto.id`
// undefined and producing the cascade of `/recording-sessions/undefined/*`
// 500s. Unwrap once here so every call site sees the DTO it expects.
type Envelope<T> = { success?: boolean; data?: T } & T;

const unwrap = <T>(payload: Envelope<T>): T => {
  if (payload && typeof payload === 'object' && 'data' in payload && payload.data !== undefined) {
    return payload.data as T;
  }
  return payload as T;
};

export const recordingSessionApi = {
  async start(payload: StartRecordingSessionPayload): Promise<RecordingSessionDTO> {
    const res = await apiClient.post<Envelope<RecordingSessionDTO>>(BASE, payload);
    return unwrap(res.data);
  },

  async mine(): Promise<RecordingSessionDTO[]> {
    const res = await apiClient.get<Envelope<RecordingSessionDTO[]>>(`${BASE}/mine`);
    return unwrap(res.data);
  },

  async get(id: string): Promise<RecordingSessionDTO> {
    const res = await apiClient.get<Envelope<RecordingSessionDTO>>(`${BASE}/${id}`);
    return unwrap(res.data);
  },

  async assignPatient(id: string, patientId: string): Promise<RecordingSessionDTO> {
    const res = await apiClient.patch<Envelope<RecordingSessionDTO>>(
      `${BASE}/${id}/patient`,
      { patientId },
    );
    return unwrap(res.data);
  },

  async appendTranscript(id: string, text: string): Promise<RecordingSessionDTO> {
    const res = await apiClient.patch<Envelope<RecordingSessionDTO>>(
      `${BASE}/${id}/transcript`,
      { text },
    );
    return unwrap(res.data);
  },

  async pause(id: string): Promise<RecordingSessionDTO> {
    const res = await apiClient.post<Envelope<RecordingSessionDTO>>(`${BASE}/${id}/pause`);
    return unwrap(res.data);
  },

  async resume(id: string): Promise<RecordingSessionDTO> {
    const res = await apiClient.post<Envelope<RecordingSessionDTO>>(`${BASE}/${id}/resume`);
    return unwrap(res.data);
  },

  async stop(id: string): Promise<RecordingSessionDTO> {
    const res = await apiClient.post<Envelope<RecordingSessionDTO>>(`${BASE}/${id}/stop`);
    return unwrap(res.data);
  },

  async abandon(id: string): Promise<RecordingSessionDTO> {
    const res = await apiClient.post<Envelope<RecordingSessionDTO>>(
      `${BASE}/${id}/abandon`,
    );
    return unwrap(res.data);
  },

  async commit(id: string): Promise<{ id: string; patientId: string; status: string }> {
    const res = await apiClient.post<Envelope<{ id: string; patientId: string; status: string }>>(
      `${BASE}/${id}/commit`,
    );
    return unwrap(res.data);
  },

  /**
   * Re-run SOAP synthesis from MinIO-stored chunks. Returns the
   * eventual medical_record id when synth succeeds. The backend can
   * respond with 409 + `code: 'AUDIO_NOT_ON_SERVER'` when no chunks
   * have been mirrored yet — the caller must surface a "open the
   * source device" hint and let the recovery driver replay from IDB.
   */
  async reprocess(id: string): Promise<{
    sessionId: string;
    medicalRecordId?: string;
    transcribedChunks: number;
  }> {
    // Override the apiClient's 30s default — reprocess pulls every chunk
    // out of MinIO, re-Whispers each (long-form on CPU is 30-60s per
    // 30s of audio), runs full-audio diarization, then streams the SOAP
    // LLM (60-90s on a busy GPU). 30s blew up before the work even
    // started transcribing the second chunk. Mirror the backend's
    // REPROCESS_TIMEOUT_MS ceiling (5 min) so the FE waits as long as
    // the server is willing to.
    const res = await apiClient.post<Envelope<{
      sessionId: string;
      medicalRecordId?: string;
      transcribedChunks: number;
    }>>(`${BASE}/${id}/reprocess`, undefined, { timeout: 5 * 60 * 1000 });
    return unwrap(res.data);
  },

  /**
   * Best-effort patient-info extraction from the session transcript.
   * Used to seed the new-patient form on encounters that started
   * unassigned. Returns an empty object when the transcript yields
   * nothing usable rather than throwing.
   */
  async extractPatientInfo(id: string): Promise<ExtractedPatientInfo> {
    const res = await apiClient.post<Envelope<ExtractedPatientInfo>>(
      `${BASE}/${id}/extract-patient-info`,
    );
    return unwrap(res.data);
  },

  /**
   * Upload one audio chunk via plain HTTP. Drain path used when the
   * live socket can't be relied on. Idempotent on (sessionId, chunkIndex)
   * — re-running the same drain is safe.
   */
  async uploadChunk(
    sessionId: string,
    chunkIndex: number,
    blob: Blob,
  ): Promise<{ sessionId: string; chunkIndex: number; uploaded: boolean }> {
    const res = await apiClient.put<Envelope<{
      sessionId: string;
      chunkIndex: number;
      uploaded: boolean;
    }>>(
      `${BASE}/${sessionId}/audio-chunks/${chunkIndex}`,
      blob,
      {
        headers: { 'Content-Type': 'application/octet-stream' },
        // Disable axios' default JSON transform — we want the raw blob
        // shipped through. axios is otherwise fine with a Blob body for
        // PUT requests.
        transformRequest: [(data) => data],
      },
    );
    return unwrap(res.data);
  },

  /**
   * Scribed-minutes billing rollup: one row per provider in the current
   * tenant whose recording sessions actually reached our STT pipeline
   * during [from, to). `from` and `to` operate on `ended_at` so a session
   * that started in March and ended in April bills against April.
   */
  async scribedMinutesSummary(opts?: {
    from?: Date;
    to?: Date;
    providerId?: string;
  }): Promise<ScribedMinutesRow[]> {
    const params: Record<string, string> = {};
    if (opts?.from) params.from = opts.from.toISOString();
    if (opts?.to) params.to = opts.to.toISOString();
    if (opts?.providerId) params.providerId = opts.providerId;
    const res = await apiClient.get<Envelope<ScribedMinutesRow[]>>(
      `${BASE}/billing/scribed-minutes`,
      { params },
    );
    return unwrap(res.data);
  },
};

export interface ScribedMinutesRow {
  providerId: string;
  providerName: string | null;
  totalSeconds: number;
  sessionCount: number;
}
